/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { GridLegacy as Grid, Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import Close from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import MDButton from "components/Basics/MDButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch, useSelector } from "react-redux";
import FormAction from "redux-react/actions/formAction";
import { v4 as uuidv4 } from "uuid";
import { display } from "redux-react/reducers/snackBarReducer";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import { useTranslation } from "react-i18next";

export default function NewsCategoriesDialog({
	open: parentOpen = false,
	categories,
	setCategories,
	selectedCategories: initialSelectedCategories = [],
	onClose,
	socket,
	onValidate,
	onCategoryDeleted,
	newsItems
}) {
	const dispatch = useDispatch();
	const [open, setOpen] = useState(parentOpen || false);
	const [editingCategory, setEditingCategory] = useState(null);
	const [newCategory, setNewCategory] = useState({ name: "", description: "", color: "#0078D4" });
	const [isAddingNew, setIsAddingNew] = useState(false);
	const [localCategories, setLocalCategories] = useState([]);
	const [selectedCategories, setSelectedCategories] = useState(initialSelectedCategories);
	const [categoryInput, setCategoryInput] = useState("");
	const [categoryNameError, setCategoryNameError] = useState("");
	const [isLoadingCategoriesNews, setIsLoadingCategoriesNews] = useState(false);
	const [originalCategory, setOriginalCategory] = useState(null);

	const { profile } = useSelector(state => state);
	const { t } = useTranslation();

	const TEAM_COLORS = [
		{ name: t("COLORS.BLUE"), value: "#0078D4" },
		{ name: t("COLORS.LIGHT_BLUE"), value: "#B3D6F2" },
		{ name: t("COLORS.LIGHT_PURPLE"), value: "#D6C7E8" },
		{ name: t("COLORS.LIGHT_PINK"), value: "#F2B8C6" },
		{ name: t("COLORS.PEACH"), value: "#F2C6B8" },
		{ name: t("COLORS.PALE_YELLOW"), value: "#F2E0B8" },
		{ name: t("COLORS.PALE_GREEN"), value: "#C6E8B8" },
		{ name: t("COLORS.DARK_BLUE"), value: "#2E5A87" },
		{ name: t("COLORS.DARK_PURPLE"), value: "#5A2E87" },
		{ name: t("COLORS.BURGUNDY"), value: "#872E5A" },
		{ name: t("COLORS.BROWN"), value: "#875A2E" },
		{ name: t("COLORS.DARK_GREEN"), value: "#5A872E" }
	];

	const handleOpenDialog = () => setOpen(true);
	const handleCloseDialog = () => {
		setOpen(false);
		setEditingCategory(null);
		setIsAddingNew(false);
		setCategoryInput("");
		setCategoryNameError("");
		onClose();
	};

	const handleValidate = () => {
		onValidate(selectedCategories);
		handleCloseDialog();
	};

	useEffect(() => {
		if (parentOpen) {
			setSelectedCategories(initialSelectedCategories);
		}
	}, [parentOpen, initialSelectedCategories]);

	useEffect(() => {
		setOpen(parentOpen);
	}, [parentOpen]);

	useEffect(() => {
		if (localCategories.length > 0 && initialSelectedCategories.length > 0) {
			const syncedSelected = localCategories.filter(cat =>
				initialSelectedCategories.some(selected => selected.code === cat.code)
			);
			setSelectedCategories(syncedSelected);
		}
	}, [localCategories, initialSelectedCategories]);

	useEffect(() => {
		if (open) {
			setIsLoadingCategoriesNews(true);
			dispatch(
				FormAction.getItemsFromCollection(
					"answerItemCategory",
					{
						query: {
							assistantID: profile.selectedAssistant.assistantID
						}
					},
					res => {
						setLocalCategories(res.items || []);
						setIsLoadingCategoriesNews(false);
					}
				)
			);
		}
	}, [open, dispatch, profile.selectedAssistant.assistantID]);

	const validateCategoryName = name => {
		return name && name.trim().length > 0;
	};

	const handleAddNew = () => {
		if (validateCategoryName(newCategory.name)) {
			const existingCategory = localCategories.find(
				cat => cat.name.toLowerCase() === newCategory.name.toLowerCase()
			);
			if (existingCategory) {
				setCategoryNameError(t("NEWS_CATEGORIES_DIALOG.CATEGORY_ALREADY_EXISTS"));
				return;
			}

			dispatch(
				FormAction.addItemEmpty(
					{
						values: {
							name: newCategory.name,
							description: newCategory.description || newCategory.name,
							color: newCategory.color,
							assistantID: profile.selectedAssistant.assistantID,
							code: uuidv4(),
							categoryId: uuidv4()
						},
						target: "answerItemCategory"
					},
					res => {
						setLocalCategories(prev => [...prev, res.item]);
						setSelectedCategories(prev => [...prev, res.item]);
						setNewCategory({ name: "", description: "", color: "#0078D4" });
						setIsAddingNew(false);
					}
				)
			);
		}
	};

	const confirmDelete = categoryId => {
		socket.emit("news_category_delete", { categoryId });

		// Écouter l'événement de suppression réussie
		socket.once("news_category_deleted", ({ categoryId, message }) => {
			setLocalCategories(prev => prev.filter(cat => cat._id !== categoryId));
			setSelectedCategories(prev => prev.filter(cat => cat._id !== categoryId));

			if (typeof onCategoryDeleted === "function") {
				onCategoryDeleted(categoryId);
			}

			dispatch(
				display({
					message: t("NEWS_CATEGORIES_DIALOG.DELETE_SUCCESS"),
					type: "success"
				})
			);
		});
	};

	const getAffectedCount = category => {
		if (!newsItems || !newsItems[0]?.data?.rows) {
			return 0;
		}

		const count = newsItems[0].data.rows.reduce((acc, row) => {
			if (row.categories && Array.isArray(row.categories)) {
				const isUsed = row.categories.some(cat => cat.code === category.code);
				return isUsed ? acc + 1 : acc;
			}
			return acc;
		}, 0);

		return count;
	};

	const isValidEdit = () => {
		if (!editingCategory) return false;

		const isNameValid = validateCategoryName(editingCategory.name);
		const isNameUnique = !localCategories.some(
			cat =>
				cat.name.toLowerCase() === editingCategory.name.toLowerCase() &&
				cat._id !== editingCategory._id
		);
		const hasChanges =
			originalCategory &&
			(editingCategory.name !== originalCategory.name ||
				editingCategory.description !== originalCategory.description ||
				editingCategory.color !== originalCategory.color);

		return isNameValid && isNameUnique && hasChanges;
	};

	const handleSaveEdit = () => {
		if (editingCategory) {
			if (!validateCategoryName(editingCategory.name)) {
				setCategoryNameError(t("NEWS_CATEGORIES_DIALOG.INVALID_CATEGORY_NAME"));
				return;
			}

			const existingCategory = localCategories.find(
				cat =>
					cat.name.toLowerCase() === editingCategory.name.toLowerCase() &&
					cat._id !== editingCategory._id
			);
			if (existingCategory) {
				setCategoryNameError(t("NEWS_CATEGORIES_DIALOG.CATEGORY_ALREADY_EXISTS"));
				return;
			}

			socket.emit("news_category_update", {
				categoryId: editingCategory._id,
				name: editingCategory.name,
				description: editingCategory.description || editingCategory.name,
				color: editingCategory.color
			});

			const updatedCategory = {
				...editingCategory,
				name: editingCategory.name,
				description: editingCategory.description || editingCategory.name,
				color: editingCategory.color
			};

			setLocalCategories(prev =>
				prev.map(cat => (cat._id === editingCategory._id ? updatedCategory : cat))
			);
			setSelectedCategories(prev =>
				prev.map(cat => (cat._id === editingCategory._id ? updatedCategory : cat))
			);

			setEditingCategory(null);
			setCategoryNameError("");
			setOriginalCategory(null);
		}
	};

	const startEditing = category => {
		setEditingCategory(category);
		setOriginalCategory(category);
		setCategoryNameError("");
	};

	useEffect(() => {
		const handleUpdateNewsCategory = updatedCategory => {
			setLocalCategories(prev =>
				prev.map(cat =>
					cat._id.toString() === updatedCategory._id.toString() ? updatedCategory : cat
				)
			);

			setSelectedCategories(prev =>
				prev.map(cat =>
					cat._id.toString() === updatedCategory._id.toString() ? updatedCategory : cat
				)
			);

			if (typeof setCategories === "function") {
				setCategories(prev =>
					prev.map(cat =>
						cat._id.toString() === updatedCategory._id.toString() ? updatedCategory : cat
					)
				);
			}
		};

		const handleDeleteSuccess = ({ message }) => {
			dispatch(
				display({
					message,
					type: "success"
				})
			);
		};

		socket.on("news_category_updated", handleUpdateNewsCategory);
		socket.on("news_category_deleted", handleDeleteSuccess);

		return () => {
			socket.off("news_category_updated", handleUpdateNewsCategory);
			socket.off("news_category_deleted", handleDeleteSuccess);
		};
	}, [socket, setCategories]);

	return (
		<Box sx={{ margin: 1 }}>
			<MDButton variant="contained" color="info" onClick={handleOpenDialog}>
				{t("NEWS_CATEGORIES_DIALOG.MANAGE_CATEGORIES_BUTTON")}
			</MDButton>
			<Dialog fullWidth maxWidth="lg" open={open} onClose={handleCloseDialog}>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h5">{t("NEWS_CATEGORIES_DIALOG.TITLE")}</Typography>
						<IconButton onClick={handleCloseDialog}>
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent dividers>
					<Box mb={4}>
						<MDButton
							variant="contained"
							color="info"
							onClick={() => setIsAddingNew(true)}
							sx={{ mb: 2 }}
						>
							{t("NEWS_CATEGORIES_DIALOG.ADD_CATEGORY_BUTTON")}
						</MDButton>

						{isAddingNew && (
							<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
								<Grid container spacing={2} direction="column">
									<Grid item xs={12} md={6}>
										<Autocomplete
											freeSolo
											options={localCategories.filter(cat => cat.color !== null)}
											getOptionLabel={option => option.name}
											inputValue={categoryInput}
											onInputChange={(event, newInputValue) => {
												setCategoryInput(newInputValue);
												setNewCategory(prev => ({ ...prev, name: newInputValue }));
												if (!validateCategoryName(newInputValue)) {
													setCategoryNameError(t("NEWS_CATEGORIES_DIALOG.INVALID_CATEGORY_NAME"));
												} else {
													setCategoryNameError("");
												}
											}}
											onChange={(event, newValue) => {
												if (typeof newValue === "string") {
													setNewCategory(prev => ({ ...prev, name: newValue }));
												} else if (newValue && newValue.name) {
													setNewCategory({
														name: newValue.name,
														description: newValue.description || newValue.name,
														color: newValue.color
													});
												}
												setCategoryNameError("");
											}}
											renderInput={params => (
												<TextField
													{...params}
													fullWidth
													label={t("NEWS_CATEGORIES_DIALOG.CATEGORY_NAME_LABEL")}
													error={!!categoryNameError}
													helperText={categoryNameError}
												/>
											)}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<TextField
											fullWidth
											label={t("NEWS_CATEGORIES_DIALOG.DESCRIPTION_LABEL")}
											value={newCategory.description}
											onChange={e =>
												setNewCategory({ ...newCategory, description: e.target.value })
											}
											placeholder={t("NEWS_CATEGORIES_DIALOG.DESCRIPTION_PLACEHOLDER")}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" gutterBottom>
											{t("NEWS_CATEGORIES_DIALOG.COLOR_LABEL")}
										</Typography>
										<Box display="flex" flexWrap="wrap" gap={1}>
											{TEAM_COLORS.map(color => (
												<Box
													key={color.value}
													onClick={() => setNewCategory({ ...newCategory, color: color.value })}
													sx={{
														width: 36,
														height: 36,
														borderRadius: "50%",
														backgroundColor: color.value,
														cursor: "pointer",
														border: newCategory.color === color.value ? "2px solid #000" : "none"
													}}
												/>
											))}
										</Box>
									</Grid>
								</Grid>
								<Box display="flex" justifyContent="flex-end" mt={2}>
									<Button onClick={() => setIsAddingNew(false)} sx={{ mr: 1 }}>
										{t("NEWS_CATEGORIES_DIALOG.CANCEL_BUTTON")}
									</Button>
									<MDButton
										variant="contained"
										color="info"
										onClick={handleAddNew}
										disabled={!newCategory.name.trim() || !!categoryNameError}
									>
										{t("NEWS_CATEGORIES_DIALOG.ADD_BUTTON")}
									</MDButton>
								</Box>
							</Paper>
						)}
					</Box>

					<Box mb={4}>
						<TableContainer component={Paper}>
							{isLoadingCategoriesNews ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: 200,
										width: "100%"
									}}
								>
									<CircularProgress color="info" />
								</Box>
							) : (
								<Table sx={{ tableLayout: "fixed" }}>
									<TableHead sx={{ display: "contents" }}>
										<TableRow>
											<TableCell sx={{ width: "10%", fontWeight: "bold" }}>
												{t("NEWS_CATEGORIES_DIALOG.TABLE_HEADER_SELECT")}
											</TableCell>
											<TableCell sx={{ width: "20%", fontWeight: "bold" }}>
												{t("NEWS_CATEGORIES_DIALOG.TABLE_HEADER_NAME")}
											</TableCell>
											<TableCell sx={{ width: "25%", fontWeight: "bold" }}>
												{t("NEWS_CATEGORIES_DIALOG.TABLE_HEADER_DESCRIPTION")}
											</TableCell>
											<TableCell sx={{ width: "15%", fontWeight: "bold" }}>
												{t("NEWS_CATEGORIES_DIALOG.TABLE_HEADER_COLOR")}
											</TableCell>
											<TableCell sx={{ width: "20%", fontWeight: "bold" }}>
												{t("NEWS_CATEGORIES_DIALOG.TABLE_HEADER_ACTIONS")}
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{localCategories.map(category => (
											<TableRow key={category._id}>
												<TableCell>
													<Checkbox
														checked={selectedCategories.some(cat => cat._id === category._id)}
														onChange={e => {
															if (e.target.checked) {
																setSelectedCategories(prev => [...prev, category]);
															} else {
																setSelectedCategories(prev =>
																	prev.filter(cat => cat._id !== category._id)
																);
															}
														}}
													/>
												</TableCell>
												<TableCell
													sx={{ wordBreak: "break-word", maxHeight: "100px", overflowY: "auto" }}
												>
													{editingCategory?._id === category._id ? (
														<TextField
															fullWidth
															value={editingCategory.name}
															onChange={e => {
																setEditingCategory({ ...editingCategory, name: e.target.value });
																// Vérifier la validité du nom en temps réel
																if (!validateCategoryName(e.target.value)) {
																	setCategoryNameError(
																		t("NEWS_CATEGORIES_DIALOG.INVALID_CATEGORY_NAME")
																	);
																} else if (
																	localCategories.some(
																		cat =>
																			cat.name.toLowerCase() === e.target.value.toLowerCase() &&
																			cat._id !== editingCategory._id
																	)
																) {
																	setCategoryNameError(
																		t("NEWS_CATEGORIES_DIALOG.CATEGORY_ALREADY_EXISTS")
																	);
																} else {
																	setCategoryNameError("");
																}
															}}
															error={!!categoryNameError}
															helperText={categoryNameError}
														/>
													) : (
														category.name
													)}
												</TableCell>
												<TableCell
													sx={{ wordBreak: "break-word", maxHeight: "100px", overflowY: "auto" }}
												>
													{editingCategory?._id === category._id ? (
														<TextField
															fullWidth
															value={editingCategory.description || editingCategory.name}
															onChange={e =>
																setEditingCategory({
																	...editingCategory,
																	description: e.target.value
																})
															}
															placeholder={t("NEWS_CATEGORIES_DIALOG.DESCRIPTION_PLACEHOLDER")}
														/>
													) : (
														category.description || category.name
													)}
												</TableCell>
												<TableCell>
													{editingCategory?._id !== category._id ? (
														<Box display="flex" alignItems="center">
															<Box
																sx={{
																	width: 30,
																	height: 30,
																	borderRadius: "50%",
																	backgroundColor: category.color,
																	mr: 1
																}}
															/>
														</Box>
													) : (
														<Box display="flex" flexWrap="wrap" gap={1}>
															{TEAM_COLORS.map(color => (
																<Box
																	key={color.value}
																	sx={{
																		width: 30,
																		height: 30,
																		borderRadius: "50%",
																		backgroundColor: color.value,
																		cursor: "pointer",
																		border:
																			editingCategory.color === color.value
																				? "2px solid black"
																				: "2px solid transparent",
																		"&:hover": { opacity: 0.8 }
																	}}
																	title={color.name}
																	onClick={() =>
																		setEditingCategory({ ...editingCategory, color: color.value })
																	}
																/>
															))}
														</Box>
													)}
												</TableCell>
												<TableCell>
													{editingCategory?._id === category._id ? (
														<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
															<MDButton
																variant="outlined"
																color="info"
																size="small"
																onClick={() => {
																	setEditingCategory(null);
																	setCategoryNameError("");
																	setOriginalCategory(null);
																}}
																sx={{ mr: 1 }}
															>
																{t("NEWS_CATEGORIES_DIALOG.CANCEL_BUTTON")}
															</MDButton>
															<MDButton
																variant="contained"
																color="info"
																size="small"
																onClick={handleSaveEdit}
																disabled={!isValidEdit()}
															>
																{t("NEWS_CATEGORIES_DIALOG.SAVE_BUTTON")}
															</MDButton>
														</Box>
													) : (
														<Box display="flex" alignItems="center" gap={1}>
															<Tooltip title={t("NEWS_CATEGORIES_DIALOG.EDIT_TOOLTIP")}>
																<IconButton size="small" onClick={() => startEditing(category)}>
																	<Edit />
																</IconButton>
															</Tooltip>
															<ConfirmDialogButton
																component={
																	<Tooltip title={t("NEWS_CATEGORIES_DIALOG.DELETE_TOOLTIP")}>
																		<IconButton size="small" color="error">
																			<Delete />
																		</IconButton>
																	</Tooltip>
																}
																onConfirm={e => {
																	confirmDelete(category._id);
																}}
																title={t("NEWS_CATEGORIES_DIALOG.DELETE_CONFIRM_TITLE")}
																getDynamicContent={rowValues => {
																	const count = getAffectedCount(rowValues);
																	if (count > 0) {
																		return t("NEWS_CATEGORIES_DIALOG.DELETE_WARNING_MESSAGE", {
																			name: rowValues.name,
																			count
																		});
																	}
																	return `Êtes-vous sûr de vouloir supprimer "${rowValues.name}" ?`;
																}}
																displayAttribute="name"
																rowValues={category}
																confirmButtonColor="error"
															/>
														</Box>
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</TableContainer>
					</Box>
				</DialogContent>

				<DialogActions>
					<MDButton variant="outlined" color="info" onClick={handleCloseDialog}>
						{t("SETTINGS.cancel")}
					</MDButton>
					<MDButton
						variant="contained"
						color="info"
						onClick={handleValidate}
						disabled={!selectedCategories.length}
					>
						{t("NEWS_CATEGORIES_DIALOG.VALIDATE_BUTTON")}
					</MDButton>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
